import React, { useEffect, useState } from "react";
import "./TableList.css";
import Input from "../reusable/Input/Input";
import Button, { ButtonTheme } from "../reusable/Button/Button";
import Switch from "../Switch/Switch";
import Modal from "../Modal/Modal";
import { 
  Page, 
  getColumnWidthByKey, 
  getColumnWidths, 
  getColumnWidthsDefault, 
  getDefaultColumnWidth, 
  saveColumnWidth,
  getVisibleColumns,
  saveVisibleColumns
} from "./TableList.helper";

interface Props {
  list: any[];
  page: Page;
  enableSearchbar?: boolean;
  columnWidth?: number;
  defaultVisibleColumns?: { [key: string]: boolean };
  defaultWidthGeneral?: number;
  extraColumnButtons?: {
    action: (record: any) => void;
    columnTitle: string;
    buttonLabel: string;
    iconName?: string;
    theme?: ButtonTheme;
    disabled?: (record: any) => boolean;
  }[];
  customColumns?: {
    columnTitle: string;
    boxContent: (data: any, index: number) => JSX.Element;
  }[];
}

function TableList({
  list = [],
  page,
  enableSearchbar = false,
  columnWidth = 150,
  defaultVisibleColumns,
  defaultWidthGeneral = 150,
  extraColumnButtons,
  customColumns,
}: Props) {
  const [listFiltered, setListFiltered] = useState(list);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [filters, setFilters] = useState<
  { filterKey: string; searchValue: string }[]
  >([]);
  const [showFilters, setShowFilters] = useState(enableSearchbar);
  const [filterValues, setFilterValues] = useState<{ [key: string]: string }>(
    {}
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showColumnWidthSelector, setShowColumnWidthSelector] = useState(false);
  const [showTableConfig, setShowTableConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<{
    [key: string]: boolean;
  }>({});
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  const [columnsInitialized, setColumnsInitialized] = useState(false);
  const titles = Object.keys(list[0] || {}).filter((title) => visibleColumns[title]);

  useEffect(() => {
    if (page) {
      const widths = getColumnWidths(page, defaultWidthGeneral, titles);
      setColumnWidths(widths);
    }
  }, [page, defaultWidthGeneral]);

  useEffect(() => {
    if (list.length > 0 && !columnsInitialized) {
      const currentColumns = Object.keys(list[0]);

      const storedVisibleColumns = getVisibleColumns(page, defaultVisibleColumns);
      const newVisibleColumns: { [key: string]: boolean } = {};
      const newColumnWidths = { ...columnWidths };

      currentColumns.forEach((key) => {

        if (storedVisibleColumns.hasOwnProperty(key)) {
          newVisibleColumns[key] = storedVisibleColumns[key];
        } else if (defaultVisibleColumns) {

          newVisibleColumns[key] = defaultVisibleColumns.hasOwnProperty(key) 
            ? defaultVisibleColumns[key] 
            : false;
        } else {

          newVisibleColumns[key] = true;
        }

        if (newColumnWidths[key] === undefined) {
          newColumnWidths[key] = getColumnWidthByKey(
            page,
            key,
            defaultWidthGeneral
          );
        }
      });

      Object.keys(newVisibleColumns).forEach((key) => {
        if (!currentColumns.includes(key)) {
          delete newVisibleColumns[key];
          delete newColumnWidths[key];
        }
      });

      setVisibleColumns(newVisibleColumns);
      setColumnWidths(newColumnWidths);
      setColumnsInitialized(true);
    }
  }, [list, defaultVisibleColumns, defaultWidthGeneral, page, columnsInitialized]);

  useEffect(() => {
    setColumnsInitialized(false);
  }, [page]);

  useEffect(() => {
    if (filters.length) {
      const newList = list.filter((record) => {
        for (const filter of filters) {
          const { filterKey, searchValue } = filter;
          const value = record[filterKey];
          const isValid =
            String(value).toUpperCase().indexOf(searchValue.toUpperCase()) > -1;
          if (!isValid) return false;
        }
        return true;
      });
      setListFiltered(newList);
    } else {
      setListFiltered(list);
    }
  }, [filters, list]);

  const filterList = (searchValue: string, filterKey: string) => {
    setFilterValues((prev) => ({ ...prev, [filterKey]: searchValue }));
    if (searchValue.length < 1) {
      const newState = filters.filter(
        (filter) => filter.filterKey !== filterKey
      );
      setFilters(newState);
    } else {
      if (filters.some((filter) => filter.filterKey === filterKey)) {
        const auxFilters = filters.filter(
          (item) => item.filterKey !== filterKey
        );
        setFilters([...auxFilters, { filterKey, searchValue }]);
      } else {
        setFilters([...filters, { filterKey, searchValue }]);
      }
    }
  };

  const renderCustomColumns = () => {
    if (!customColumns) {
      return <></>;
    } else {
      const buttons = customColumns.map((customColumn) => {
        return (
          <div className="columnCustomContainer justify-center" key={customColumn.columnTitle}>
            <div className="headerContainer">
              <div className="titleBox">
                <b className="texto-en-linea">{customColumn.columnTitle}</b>
              </div>
              {showFilters && <div className="filterBox border-b-2 border-grey" />}
            </div>
            <div className="contentContainer">
              {listFiltered?.map((object, index) => (
                <div
                  className={`item ${hoveredIndex === index ? "row-hover" : ""}`}
                  key={index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="item-content">
                    {customColumn.boxContent(object, index)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      });
      return buttons;
    }
  };

  const renderColumnButton = () => {
    if (!extraColumnButtons) {
      return <></>;
    } else {
      const buttons = extraColumnButtons.map((buttonColumn) => {
        return (
          <div className="columnContainer" key={buttonColumn.columnTitle}>
            <div className="headerContainer">
              <div className="titleBox">
                <b className="texto-en-linea">{buttonColumn.columnTitle}</b>
              </div>
              {showFilters && (
                <div className="filterBox border-b-2 border-grey" />
              )}
            </div>
            <div className="contentContainer">
              {listFiltered?.map((object, index) => {
                const isDisabled = buttonColumn.disabled?.(object) || false;
                return (
                  <div
                    className={`item ${hoveredIndex === index ? "row-hover" : ""}`}
                    key={object.id}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div className="item-content">
                      <Button
                        theme={buttonColumn.theme || "primary"}
                        label={buttonColumn.buttonLabel}
                        iconName={buttonColumn.iconName}
                        className="h-5 texto-en-linea w-full"
                        onClick={() => buttonColumn.action(object)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      });
      return buttons;
    }
  };

  const renderDataToString = (data: any) => {
    if (typeof data === "string") {
      return data;
    } else if (typeof data === "object") {
      return JSON.stringify(data);
    }
    return data;
  };

  const clearFilters = () => {
    setFilters([]);
    setListFiltered(list);
    setFilterValues({});
  };

  const toggleColumn = (columnTitle: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnTitle]: !visibleColumns[columnTitle],
    };
    setVisibleColumns(newVisibleColumns);

    saveVisibleColumns(page, newVisibleColumns);
  };

  const toggleAllColumns = (show: boolean) => {
    const newVisibleColumns = Object.keys(visibleColumns).reduce((acc, key) => {
      acc[key] = show;
      return acc;
    }, {} as { [key: string]: boolean });
    setVisibleColumns(newVisibleColumns);

    saveVisibleColumns(page, newVisibleColumns);
  };

  const handleColumnWidthChange = (columnTitle: string, width: number) => {
    const newWidths = { ...columnWidths, [columnTitle]: width };
    setColumnWidths(newWidths);
    saveColumnWidth(page, columnTitle, width);
  };

  const handleResetColumnWidths = () => {
    if (page) {
      const defaultWidths = getColumnWidthsDefault(
        page,
        defaultWidthGeneral,
        titles
      );
      setColumnWidths(defaultWidths);
      Object.entries(defaultWidths).forEach(([columnTitle, width]) => {
        saveColumnWidth(page, columnTitle, width as number);
      });
    }
  };

  const renderColumnSelector = () => {
    const allVisible = Object.values(visibleColumns).every((v) => v);
    const allHidden = Object.values(visibleColumns).every((v) => !v);

    return (
      <Modal
        open={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        message=""
        closeButton={{
          label: "Cerrar",
          buttonFn: () => setShowColumnSelector(false),
        }}
      >
        <div className="column-selector-content">
          <div className="flex flex-col gap-4 mb-4">
            <h3 className="text-lg font-medium">Columnas visibles</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(visibleColumns).map((columnTitle) => (
              <div key={columnTitle} className="column-selector-item">
                <Switch
                  checked={visibleColumns[columnTitle]}
                  onChange={() => toggleColumn(columnTitle)}
                  label={columnTitle}
                  size="small"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-4 border-t pt-4">
            <Switch
              checked={allVisible}
              onChange={() => toggleAllColumns(!allVisible)}
              label="Mostrar todas las columnas"
              size="small"
              className="text-primary"
            />
          </div>
        </div>
      </Modal>
    );
  };

  const renderColumnWidthSelector = () => {
    return (
      <Modal
        open={showColumnWidthSelector}
        onClose={() => setShowColumnWidthSelector(false)}
        message=""
        closeButton={{
          label: "Cerrar",
          buttonFn: () => setShowColumnWidthSelector(false),
        }}
      >
        <div className="column-selector-content">
          <div className="flex flex-col gap-4 mb-4">
            <h3 className="text-lg font-medium">Ajustar ancho de columnas</h3>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {Object.keys(columnWidths).map((columnTitle) => (
              <div key={columnTitle} className="column-selector-item">
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium">{columnTitle}</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={String(columnWidths[columnTitle])}
                      onChange={(value) => handleColumnWidthChange(columnTitle, Number(value))}
                      className="w-24"
                      size="small"
                    />
                    <span className="text-sm text-gray-500">px</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    );
  };

  if (!list?.length) {
    return <></>;
  }

  return (
    <div className="tableContainer">
      {/* SECCION FILTROS */}
      <div className="filters-controls">
        <div className="flex items-center gap-2">
          <Switch
            checked={showFilters}
            onChange={() => setShowFilters(!showFilters)}
            label="Mostrar filtros de página"
            size="small"
          />
        </div>
        <Button
          theme="primary"
          label="Limpiar Filtros"
          onClick={clearFilters}
          disabled={filters.length === 0}
          iconName="eraser"
        />
        <Button
          theme="default"
          label="Configurar tabla"
          onClick={() => setShowTableConfig(!showTableConfig)}
          iconName="gear"
        />
        {showTableConfig && (
          <>
            <Button
              theme="default"
              label="Mostrar columnas"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              iconName="toggles"
            />
            <Button
              theme="default"
              label="Ajustar ancho de columnas"
              onClick={() => setShowColumnWidthSelector(!showColumnWidthSelector)}
              iconName="columns-gap"
            />
            <Button
              theme="default"
              label="Restablecer ancho por defecto"
              onClick={handleResetColumnWidths}
              iconName="layout-three-columns"
            />
          </>
        )}
      </div>
      {/* SECCION COLUMNAS */}
      {renderColumnSelector()}
      {renderColumnWidthSelector()}
      {/* SECCION LISTA */}
      <div className="scrollContainer">
        <div className="titleTable">
          {renderCustomColumns()}
          {renderColumnButton()}
          {titles.map((columnTitle) => (
            <div
              className="columnContainer"
              key={columnTitle}
              style={{ width: `${columnWidths[columnTitle] || defaultWidthGeneral}px` }}
            >
              <div className="headerContainer">
                <div className="titleBox ">
                  <b>{columnTitle}</b>
                </div>
                {showFilters && (
                  <div className="filterBox">
                    <Input
                      placeholder={`filtrar ${columnTitle}`}
                      size="small"
                      className="w-full"
                      onChange={(text) => filterList(text, columnTitle)}
                      value={filterValues[columnTitle] || ""}
                    />
                  </div>
                )}
              </div>
              <div className="contentContainer">
                {listFiltered?.map((object, index) => (
                  <div
                    className={`item ${hoveredIndex === index ? "row-hover" : ""}`}
                    key={object.id}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div className="item-content">
                      <p className="texto-en-linea">
                        {renderDataToString(object[columnTitle])}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showFilters && (
        <div className="footer-info">
          Mostrando {listFiltered.length} registros filtrados ({list.length}{" "}
          registros totales en página)
        </div>
      )}
    </div>
  );
}

export default TableList;